import { Injectable, Inject } from '@angular/core';
import { DA_SERVICE_TOKEN, ITokenService } from '@delon/auth';
import { SettingsService } from '@delon/theme';

export interface WatermarkConfig {
  /** 水印文字（不传则自动根据登录状态生成） */
  text?: string;
  /** 水印颜色 */
  color?: string;
  /** 水印字体大小 */
  fontSize?: number;
  /** 水印透明度 (0-1) */
  opacity?: number;
  /** 水印宽度 */
  width?: number;
  /** 水印高度 */
  height?: number;
  /** 水印水平间距 */
  gapX?: number;
  /** 水印垂直间距 */
  gapY?: number;
  /** 水印旋转角度 */
  rotate?: number;
  /** 水印字体 */
  fontFamily?: string;
  /** zIndex */
  zIndex?: number;
}

/** 默认水印配置（不含 text，text 由动态逻辑生成） */
const DEFAULT_CONFIG: WatermarkConfig & { text: string } = {
  text: '',
  color: 'rgba(0, 0, 0, 0.12)',
  fontSize: 16,
  opacity: 1,
  width: 200,
  height: 200,
  gapX: 120,
  gapY: 80,
  rotate: -22,
  fontFamily: 'Microsoft YaHei, sans-serif',
  zIndex: 99999
};

/** 未登录时水印文字前缀 */
const WATERMARK_PREFIX = '王力安防';

@Injectable({
  providedIn: 'root'
})
export class WatermarkService {
  private container: HTMLElement | null = null;
  private canvasEl: HTMLCanvasElement | null = null;
  private currentConfig: WatermarkConfig & { text: string } = { ...DEFAULT_CONFIG };
  private mutationObserver: MutationObserver | null = null;

  constructor(
    @Inject(DA_SERVICE_TOKEN) private tokenService: ITokenService,
    private settings: SettingsService,
  ) {}

  /**
   * 根据登录状态动态生成水印文字
   * - 已登录且有姓名 → "王力安防-姓名"
   * - 其他情况     → "王力安防"
   */
  private generateWatermarkText(): string {
    const tokenData = this.tokenService.get();
    const token = tokenData ? tokenData.token : null;
    const user = this.settings.user;
    const userName = user && user.name ? user.name : '';

    if (token && userName) {
      return WATERMARK_PREFIX + '-' + userName;
    }
    return WATERMARK_PREFIX;
  }

  /**
   * 创建水印（全局使用，自动处理动态文字）
   * @param config 水印样式配置（可选，text 不传则自动根据登录状态生成）
   * @param container 容器元素（默认 body）
   */
  create(config?: WatermarkConfig, container?: HTMLElement): void {
    const finalText = config && config.text !== undefined ? config.text : this.generateWatermarkText();

    this.currentConfig = { ...this.currentConfig, ...config, text: finalText };

    if (!container) {
      container = document.body;
    }
    this.container = container;

    // 移除旧水印
    this.remove();

    const canvas = document.createElement('canvas');

    const cfg = this.currentConfig;
    canvas.width = (cfg.width + cfg.gapX);
    canvas.height = (cfg.height + cfg.gapY);

    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 将坐标系平移到画布中心并旋转
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((cfg.rotate * Math.PI) / 180);
    ctx.globalAlpha = cfg.opacity;
    ctx.font = cfg.fontSize + 'px ' + cfg.fontFamily;
    ctx.fillStyle = cfg.color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // 绘制多行水印文字
    const lines = cfg.text.split('\n');
    const lineHeight = cfg.fontSize * 1.5;
    const startY = -((lines.length - 1) * lineHeight) / 2;
    lines.forEach(function(line, index) {
      ctx.fillText(line, 0, startY + index * lineHeight);
    });

    ctx.restore();

    // 创建水印 div
    const watermarkDiv = document.createElement('div');
    watermarkDiv.id = '__watermark_div';
    watermarkDiv.style.pointerEvents = 'none';
    watermarkDiv.style.position = 'fixed';
    watermarkDiv.style.top = '0';
    watermarkDiv.style.left = '0';
    watermarkDiv.style.width = '100vw';
    watermarkDiv.style.height = '100vh';
    watermarkDiv.style.zIndex = String(cfg.zIndex);
    watermarkDiv.style.backgroundImage = 'url(' + canvas.toDataURL('image/png') + ')';
    watermarkDiv.style.backgroundRepeat = 'repeat';

    // 确保容器有相对定位（非 body 时）
    if (container !== document.body) {
      const position = getComputedStyle(container).position;
      if (position === 'static') {
        container.style.position = 'relative';
      }
      watermarkDiv.style.position = 'absolute';
      watermarkDiv.style.width = '100%';
      watermarkDiv.style.height = '100%';
    } else {
      // body 场景：确保 html/body 有高度，防止水印不显示
      const htmlEl = document.documentElement;
      const htmlHeight = getComputedStyle(htmlEl).height;
      if (htmlHeight === '0px' || htmlHeight === 'auto') {
        htmlEl.style.minHeight = '100%';
      }
      const bodyHeight = getComputedStyle(document.body).height;
      if (bodyHeight === '0px' || bodyHeight === 'auto') {
        document.body.style.minHeight = '100vh';
      }
    }

    container.appendChild(watermarkDiv);

    // 监听 DOM 变化，防止水印被删除
    this.observeWatermark(container, watermarkDiv);
  }

  /**
   * 刷新水印（重新读取登录状态并重建）
   * 用于登录/登出后调用
   */
  refresh(): void {
    this.create(this.currentConfig, this.container || undefined);
  }

  // ==================== 开发测试辅助方法 ====================

  /**
   * 移除水印
   */
  remove(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    const existingDiv = document.getElementById('__watermark_div');
    if (existingDiv) {
      existingDiv.remove();
    }
    this.canvasEl = null;
  }

  /**
   * 更新水印配置并重新创建
   */
  update(config: WatermarkConfig): void {
    this.create(config, this.container || undefined);
  }

  /**
   * 获取当前配置
   */
  getConfig(): Readonly<WatermarkConfig> {
    return { ...this.currentConfig };
  }

  /**
   * 使用 MutationObserver 监听水印节点，防止被篡改或删除
   */
  private observeWatermark(container: HTMLElement, watermarkDiv: HTMLElement): void {
    if (typeof MutationObserver === 'undefined') return;

    this.mutationObserver = new MutationObserver((mutations) => {
      let needRecreate = false;

      for (let i = 0; i < mutations.length; i++) {
        const mutation = mutations[i];
        // 检查水印节点是否被移除
        if (mutation.type === 'childList') {
          const removedNodes = Array.from(mutation.removedNodes);
          for (let j = 0; j < removedNodes.length; j++) {
            if ((removedNodes[j] as HTMLElement).id === '__watermark_div') {
              needRecreate = true;
              break;
            }
          }
        }
        // 检查水印属性被修改
        if (mutation.type === 'attributes' && (mutation.target as HTMLElement).id === '__watermark_div') {
          needRecreate = true;
          break;
        }
      }

      if (needRecreate && container.contains(watermarkDiv) === false) {
        this.create(this.currentConfig, container);
      }
    });

    this.mutationObserver.observe(container, {
      childList: true,
      subtree: false,
      attributes: true,
      attributeFilter: ['style', 'class', 'id']
    });
  }
}
